import { NameCell, PhoneCell, TotalsLabel } from "@";
import { formatNumber } from "@/utils/formatting.js";
import type { QualityStatistic } from "../types.ts";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/shared/components/tanstackTable";
import { useQualityStatisticsQuery } from "../api/queries";
import RefetchDataOnError from "./RefetchData.tsx";

const QUALITY_TOTAL_ROW_ID = "quality-total-row";

const isTotalRow = (id?: string) => id === QUALITY_TOTAL_ROW_ID;

// Driver name — TotalsLabel pill on the synthetic totals row, NameCell otherwise.
const DriverNameCell = ({
  row,
}: {
  row: { original: QualityStatistic };
}) =>
  isTotalRow(row.original.driverId) ? (
    <TotalsLabel />
  ) : (
    <NameCell name={row.original.driverName} />
  );

// Stats number with isTotal-aware bold + tone. Same pattern as Assignment;
// the shared AmountCell uses different tone semantics (only colors when > 0).
const NumberCell = ({
  value,
  isTotal,
  tone = "default",
}: {
  value: number;
  isTotal: boolean;
  tone?: "default" | "emerald" | "rose";
}) => {
  const totalClass =
    tone === "emerald"
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "rose"
        ? "text-rose-700 dark:text-rose-300"
        : "text-purple-900 dark:text-purple-300";
  return (
    <span
      className={`tabular-nums ${
        isTotal
          ? `font-bold ${totalClass}`
          : "font-medium text-gray-700 dark:text-gray-300"
      }`}
    >
      {formatNumber(value)}
    </span>
  );
};

// Percentage cell with a small inline progress bar. Suppressed on totals row
// (the percentage doesn't aggregate meaningfully).
const PercentCell = ({
  value,
  isTotal,
  tone,
}: {
  value: number;
  isTotal: boolean;
  tone: "emerald" | "rose";
}) => {
  if (isTotal)
    return <span className="text-gray-300 dark:text-gray-600">—</span>;
  const trackBg =
    tone === "emerald"
      ? "bg-emerald-100 dark:bg-emerald-950/40"
      : "bg-rose-100 dark:bg-rose-950/40";
  const fillBg =
    tone === "emerald"
      ? "from-emerald-400 to-emerald-600 dark:from-emerald-500 dark:to-emerald-700"
      : "from-rose-400 to-rose-600 dark:from-rose-500 dark:to-rose-700";
  const text =
    tone === "emerald"
      ? "text-emerald-700 dark:text-emerald-300"
      : "text-rose-700 dark:text-rose-300";
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="flex flex-col gap-1">
      <span className={`text-xs font-bold tabular-nums tracking-tight ${text}`}>
        {formatNumber(pct)}%
      </span>
      <span
        className={`relative block h-1.5 w-full overflow-hidden rounded-full ${trackBg}`}
      >
        <span
          className={`absolute inset-y-0 start-0 bg-gradient-to-r ${fillBg}`}
          style={{ width: `${pct}%` }}
        />
      </span>
    </div>
  );
};

const qualityTableColumns: ColumnDef<QualityStatistic>[] = [
  {
    id: "driverName",
    header: "اسم المندوب",
    size: 180,
    cell: ({ row }) => (
      <DriverNameCell row={row as { original: QualityStatistic }} />
    ),
  },
  {
    id: "phoneNumber",
    header: "رقم الجوال",
    size: 140,
    cell: ({ row }) => <PhoneCell phone={row.original.phoneNumber} />,
  },
  {
    id: "approvedFromFirstTimeCount",
    header: "العمليات المعتمدة من أول مرة",
    size: 170,
    cell: ({ row }) => (
      <NumberCell
        value={row.original.approvedFromFirstTimeCount}
        isTotal={isTotalRow(row.original.driverId)}
        tone="emerald"
      />
    ),
  },
  {
    id: "totalRejectionCount",
    header: "عدد مرات الإرجاع",
    size: 140,
    cell: ({ row }) => (
      <NumberCell
        value={row.original.totalRejectionCount}
        isTotal={isTotalRow(row.original.driverId)}
        tone="rose"
      />
    ),
  },
  {
    id: "successPercentage",
    header: "معدل الطلبات الناجحة",
    size: 150,
    cell: ({ row }) => (
      <PercentCell
        value={row.original.successPercentage}
        isTotal={isTotalRow(row.original.driverId)}
        tone="emerald"
      />
    ),
  },
  {
    id: "rejectionPercentage",
    header: "معدل الإرجاع",
    size: 150,
    cell: ({ row }) => (
      <PercentCell
        value={row.original.rejectionPercentage}
        isTotal={isTotalRow(row.original.driverId)}
        tone="rose"
      />
    ),
  },
];

const QualityStatistics = ({
  fromDate,
  toDate,
}: {
  fromDate: string;
  toDate: string;
}) => {
  const {
    data: qualityData,
    isLoading: isLoadingQuality,
    isError,
    refetch,
  } = useQualityStatisticsQuery(fromDate, toDate);

  if (isError) {
    return <RefetchDataOnError refetch={refetch} />;
  }

  const qualityDataWithTotals =
    qualityData && qualityData.length > 0
      ? [
          ...qualityData,
          {
            driverId: QUALITY_TOTAL_ROW_ID,
            driverName: "الإجمالي",
            phoneNumber: "",
            approvedFromFirstTimeCount: qualityData.reduce(
              (total, item) => total + item.approvedFromFirstTimeCount,
              0,
            ),
            totalRejectionCount: qualityData.reduce(
              (total, item) => total + item.totalRejectionCount,
              0,
            ),
            successPercentage: 0,
            rejectionPercentage: 0,
          },
        ]
      : qualityData;

  return (
    <DataTable
      className="card relative overflow-hidden"
      data={qualityDataWithTotals}
      columns={qualityTableColumns}
      isLoading={isLoadingQuality}
    />
  );
};

export default QualityStatistics;
